import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WhatsappBusinessCloudTemplates from './WhatsappBusinessCloudTemplates';
import { useAppSelector } from '../../redux/store';
import SideNavbar from '../../components/SideNavbar';
import { db } from '../../firebaseConfig';
import Toast from '../../components/Toast';
import { 
    collection, 
    doc, 
    setDoc, 
    getDoc, 
    getDocs, 
    deleteDoc, 
    query, 
    where, 
    serverTimestamp, 
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { useAuth } from '../../AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Constants for localStorage keys and token expiration
const LOCAL_STORAGE_KEYS = {
  ACCESS_TOKEN: 'whatsapp_business_cloud_access_token',
  TOKEN_EXPIRY: 'whatsapp_business_cloud_token_expiry'
};

// Default token expiration in seconds (60 days)
const DEFAULT_TOKEN_EXPIRATION = 60 * 24 * 60 * 60;

interface WhatsAppNumber {
  id: string;
  display_phone_number: string;
  verified_name: string;
  quality_rating?: string;
  status?: string;
}

interface WhatsAppAccount {
  id: string;
  name: string;
  business_name?: string;
  numbers?: WhatsAppNumber[];
  connectedDate?: string;
}

interface Business {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  companyId: string;
  photoURL?: string;
}

const WhatsappBusinessCloud = () => {
    const { currentUser, userData } = useAuth();
    const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingType, setLoadingType] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [tokenExpired, setTokenExpired] = useState(false);
    
    // Business selection step
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
    
    // WhatsApp account selection step
    const [whatsappAccounts, setWhatsappAccounts] = useState<WhatsAppAccount[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
    
    // Phone number selection step
    const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppNumber[]>([]);
    const [selectedNumberId, setSelectedNumberId] = useState<string | null>(null);
    
    // User assignment
    const [selectedUsers, setSelectedUsers] = useState<{[key: string]: boolean}>({});
    const [numberAssignedUsers, setNumberAssignedUsers] = useState<{[numberId: string]: string[]}>({});
    const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
    const [userSearch, setUserSearch] = useState('');
    const [companyUsers, setCompanyUsers] = useState<User[]>([]);
    const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false);
    const [currentNumberBeingAssigned, setCurrentNumberBeingAssigned] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<string>('');
    const [isErrorToast, setIsErrorToast] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    
    // Templates management
    const [showTemplatesModal, setShowTemplatesModal] = useState<boolean>(false);
    const [currentTemplateAccount, setCurrentTemplateAccount] = useState<{id: string, name: string} | null>(null);
    
    // Connected accounts
    const [connectedAccounts, setConnectedAccounts] = useState<WhatsAppAccount[]>([]);

    const handleGoBack = () => {
        navigate('/integrations');
    };
    
    // Function to get the actual token expiration from Facebook
    const getTokenExpiration = async (token: string): Promise<Date> => {
        try {
            // Use the debug_token endpoint to get information about the token
            const debugTokenResponse = await fetch(
                `https://graph.facebook.com/v22.0/debug_token?input_token=${token}&access_token=${token}`
            );
            
            if (!debugTokenResponse.ok) {
                // If we can't get the expiration, default to 60 days
                return new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
            }
            
            const tokenData = await debugTokenResponse.json();
            
            // Check if we have a valid expiration timestamp
            if (tokenData.data && tokenData.data.expires_at) {
                // expires_at is in seconds since epoch
                return new Date(tokenData.data.expires_at * 1000);
            } else {
                // Default to 60 days if no expiration found
                return new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
            }
        } catch (error) {
            console.error('Error getting token expiration:', error);
            // Default to 60 days if error
            return new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
        }
    };
    
    // Helper function to check if a Facebook account can be used with this company
    const checkCompanyFacebookAccount = async (token: string): Promise<boolean> => {
        try {
            // Get user ID from Meta API
            const userResponse = await fetch(
                `https://graph.facebook.com/v22.0/me?access_token=${token}`
            );
            
            if (!userResponse.ok) {
                throw new Error('Failed to get user ID from Facebook');
            }
            
            const fbUserData = await userResponse.json();
            const accountId = fbUserData.id;
            
            // Check if there's already an account for this company
            if (!userData?.companyId) {
                throw new Error('No company ID found for current user');
            }
            
            const existingAccountsQuery = query(
                collection(db, 'whatsappBusinessCloudAccounts'),
                where('companyId', '==', userData.companyId)
            );
            
            const existingAccountsSnapshot = await getDocs(existingAccountsQuery);
            
            // If there's already an account for this company and it's not the current user's account
            if (!existingAccountsSnapshot.empty && !existingAccountsSnapshot.docs.some(doc => doc.id === accountId)) {
                const existingAccountDoc = existingAccountsSnapshot.docs[0];
                const existingAccountData = existingAccountDoc.data();
                
                // Extract available information about the existing admin
                const existingAdminId = existingAccountDoc.id;
                const existingAdminName = existingAccountData.accountName || 'Unknown';
                
                // Show detailed error message with account information
                setToastMessage(`This company already has a WhatsApp Business admin (${existingAdminName}, ID: ${existingAdminId}). Only the existing admin can make changes.`);
                setIsErrorToast(true);
                setShowToast(true);
                
                // Clear the token
                clearTokenFromStorage();
                setAccessToken(null);
                
                return false;
            }
            
            return true;
        } catch (error) {
            console.error('Error in checkCompanyFacebookAccount:', error);
            throw error; // Re-throw to be handled by the calling function
        }
    };
    
    // Check if the logged-in Facebook account is associated with the company
    const verifyFacebookAccount = async (token: string) => {
        setIsLoading(true);
        setLoadingType('verifying');
        
        try {
            const isVerified = await checkCompanyFacebookAccount(token);
            setIsLoading(false);
            return isVerified;
        } catch (error) {
            console.error('Error verifying Facebook account:', error);
            setToastMessage('Failed to verify Facebook account association with your company.');
            setIsErrorToast(true);
            setShowToast(true);
            setIsLoading(false);
            return false;
        }
    };
    
    // Save token to localStorage with expiration and to Firestore
    const saveTokenToStorage = async (token: string) => {
        // Save to Firestore if user is logged in
        if (userData?.companyId) {
            try {
                // Check if this account can be used with this company
                const isAllowed = await checkCompanyFacebookAccount(token);
                if (!isAllowed) {
                    return; // Exit if verification failed
                }
                
                // Get user ID from Meta API
                const userResponse = await fetch(
                    `https://graph.facebook.com/v22.0/me?access_token=${token}`
                );
                
                if (!userResponse.ok) {
                    throw new Error('Failed to get user ID from Facebook');
                }
                
                const fbUserData = await userResponse.json();
                const accountId = fbUserData.id;
                
                // Save token to localStorage
                const now = new Date();
                const expiryTime = now.getTime() + (DEFAULT_TOKEN_EXPIRATION * 1000);
                
                localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
                localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
                
                // Get the actual expiration date from Facebook
                const expirationDate = await getTokenExpiration(token);
                
                // Save to Firestore
                await setDoc(doc(db, 'whatsappBusinessCloudAccounts', accountId), {
                    accountId,
                    accountName: fbUserData.name || 'WhatsApp Business Account',
                    accountAccessToken: token,
                    // Use the actual expiration date from Facebook
                    accountExpiration: expirationDate,
                    companyId: userData.companyId,
                    updatedAt: serverTimestamp()
                }, { merge: true });
                
                // WhatsApp Business Cloud account saved successfully
            } catch (error) {
                console.error('Error saving token to Firestore:', error);
            }
        }
    };
    
    // Clear token from localStorage
    const clearTokenFromStorage = () => {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
    };
    
    // Check if token is expired
    const isTokenExpired = () => {
        const expiryTime = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
        if (!expiryTime) return true;
        
        const now = new Date().getTime();
        return now > parseInt(expiryTime);
    };
    
    // Connect to Facebook for WhatsApp Business Cloud API
    const handleConnectToFacebook = () => {
        setTokenExpired(false);
        const fbOAuthUrl = new URL('https://www.facebook.com/v22.0/dialog/oauth');
        
        // Add query parameters
        fbOAuthUrl.searchParams.append('response_type', 'token');
        fbOAuthUrl.searchParams.append('display', 'popup');
        fbOAuthUrl.searchParams.append('client_id', '1201840001461838');
        fbOAuthUrl.searchParams.append('redirect_uri', `${window.location.origin}/integrations/whatsapp-business-cloud`);
        fbOAuthUrl.searchParams.append('auth_type', 'rerequest');
        fbOAuthUrl.searchParams.append('scope', 'whatsapp_business_management,whatsapp_business_messaging,business_management');
        
        // Redirect to Facebook OAuth dialog
        window.location.href = fbOAuthUrl.toString();
    };
    
    // Disconnect from Facebook (frontend only)
    const handleDisconnectFacebook = () => {
        // Clear token from localStorage
        clearTokenFromStorage();
        
        // Reset frontend state
        setAccessToken(null);
        setWhatsappNumbers([]);
        setSelectedNumberId(null);
        setSelectedUsers({});
        setNumberAssignedUsers({});
        
        // Disconnected from WhatsApp Business Cloud API
    };
    
    // Fetch businesses associated with the account
    const fetchBusinesses = async () => {
        if (!accessToken) return;
        
        try {
            setLoadingType('businesses');
            setErrorMessage(null);
            
            // Get user details
            const userResponse = await fetch(
                `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
            );
            
            if (!userResponse.ok) {
                const errorData = await userResponse.json();
                
                // Check for expired token error
                if (errorData.error?.code === 190 || errorData.error?.message?.includes('expired')) {
                    setTokenExpired(true);
                    clearTokenFromStorage();
                    setAccessToken(null);
                    throw new Error('Your Facebook session has expired. Please reconnect to continue.');
                }
                
                throw new Error(errorData.error?.message || 'Failed to fetch user data');
            }
            
            const userData = await userResponse.json();

            
            // Get businesses associated with the user
            const businessResponse = await fetch(
                `https://graph.facebook.com/v22.0/me/businesses?access_token=${accessToken}`
            );
            
            if (!businessResponse.ok) {
                const errorData = await businessResponse.json();
                throw new Error(errorData.error?.message || 'Failed to fetch business accounts');
            }
            
            const businessData = await businessResponse.json();

            
            if (businessData.data && Array.isArray(businessData.data) && businessData.data.length > 0) {
                const businessList: Business[] = businessData.data.map((business: any) => ({
                    id: business.id,
                    name: business.name
                }));
                
                setBusinesses(businessList);
                // Reset account and number selections
                setSelectedBusinessId(null);
                setWhatsappAccounts([]);
                setSelectedAccountId(null);
                setWhatsappNumbers([]);
                setSelectedNumberId(null);
            } else {
                setErrorMessage('No businesses found for your account. Please make sure you have access to a Meta Business account.');
            }
            
            setLoadingType(null);
        } catch (error: any) {
            console.error('Error fetching businesses:', error);
            setErrorMessage(error.message || 'Failed to fetch businesses');
            setLoadingType(null);
        }
    };
    
    // Fetch WhatsApp Business accounts for a selected business
    const fetchWhatsAppAccounts = async (businessId: string) => {
        if (!accessToken || !businessId) return;
        
        try {
            setLoadingType('accounts');
            setErrorMessage(null);
            
            const wababResponse = await fetch(
                `https://graph.facebook.com/v22.0/${businessId}/owned_whatsapp_business_accounts?access_token=${accessToken}`
            );
            
            if (!wababResponse.ok) {
                const errorData = await wababResponse.json();
                throw new Error(errorData.error?.message || 'Failed to fetch WhatsApp Business accounts');
            }
            
            const wababData = await wababResponse.json();

            
            if (wababData.data && Array.isArray(wababData.data) && wababData.data.length > 0) {
                const accountsList: WhatsAppAccount[] = wababData.data.map((account: any) => ({
                    id: account.id,
                    name: account.name || 'WhatsApp Business Account',
                    business_name: account.currency || ''
                }));
                
                setWhatsappAccounts(accountsList);
                // Reset number selection
                setSelectedAccountId(null);
                setWhatsappNumbers([]);
                setSelectedNumberId(null);
            } else {
                setErrorMessage('No WhatsApp Business accounts found for this business. Please make sure your business has a WhatsApp Business account.');
            }
            
            setLoadingType(null);
        } catch (error: any) {
            console.error('Error fetching WhatsApp Business accounts:', error);
            setErrorMessage(error.message || 'Failed to fetch WhatsApp Business accounts');
            setLoadingType(null);
        }
    };
    
    // Fetch phone numbers for a selected WhatsApp Business account
    const fetchWhatsAppNumbers = async (accountId: string) => {
        if (!accessToken || !accountId) return;
        
        try {
            setLoadingType('numbers');
            setErrorMessage(null);
            
            const numbersResponse = await fetch(
                `https://graph.facebook.com/v22.0/${accountId}/phone_numbers?access_token=${accessToken}`
            );
            
            if (!numbersResponse.ok) {
                const errorData = await numbersResponse.json();
                throw new Error(errorData.error?.message || 'Failed to fetch WhatsApp phone numbers');
            }
            
            const numbersData = await numbersResponse.json();

            
            if (numbersData.data && Array.isArray(numbersData.data) && numbersData.data.length > 0) {
                setWhatsappNumbers(numbersData.data);
                setSelectedNumberId(null);
                
                // Load existing user assignments for these numbers
                const account = whatsappAccounts.find(a => a.id === accountId);
                if (account) {
                    // Get the Facebook user ID to use as the account ID
                    try {
                        const userResponse = await fetch(
                            `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
                        );
                        
                        if (userResponse.ok) {
                            const fbUserData = await userResponse.json();
                            const fbAccountId = fbUserData.id;
                            
                            // Load user assignments with the fetched numbers directly
                            await loadUserAssignments(fbAccountId, account.id, numbersData.data);
                        }
                    } catch (error) {
                        console.error('Error loading user assignments:', error);
                    }
                }
                
                // Save the phone numbers to Firestore if user is logged in
                if (userData?.companyId) {
                    try {
                        // Get user ID to use as account ID
                        const userResponse = await fetch(
                            `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
                        );
                        
                        if (userResponse.ok) {
                            const fbUserData = await userResponse.json();
                            const fbAccountId = fbUserData.id;
                            
                            // Create a batch to save all data
                            // Store all data in state instead of saving to Firestore immediately
                            // We'll only save data when users are assigned
                            
                            // Store account info
                            const accountInfo = {
                                accountId: fbAccountId,
                                accountName: fbUserData.name || 'WhatsApp Business Account',
                                accountAccessToken: accessToken
                            };
                            
                            // Store business info
                            const business = businesses.find(b => b.id === selectedBusinessId);
                            const businessInfo = business ? {
                                businessId: business.id,
                                name: business.name,
                                companyId: userData.companyId,
                                updatedAt: serverTimestamp()
                            } : null;
                            
                            // Store WhatsApp account info
                            const account = whatsappAccounts.find(a => a.id === accountId);
                            const accountInfo2 = account ? {
                                wabaId: account.id,
                                name: account.name,
                                companyId: userData.companyId,
                                updatedAt: serverTimestamp()
                            } : null;
                            
                            // Store phone numbers in state for later assignment
                            setWhatsappNumbers(numbersData.data.map((number: WhatsAppNumber) => ({
                                id: number.id,
                                display_phone_number: number.display_phone_number,
                                verified_name: number.verified_name,
                                quality_rating: number.quality_rating || 'UNKNOWN',
                                status: number.status || 'CONNECTED'
                            })));
                            
                
                        }
                    } catch (error) {
                        console.error('Error saving phone numbers to Firestore:', error);
                    }
                }
            } else {
                setErrorMessage('No WhatsApp phone numbers found for this account.');
                setWhatsappNumbers([]);
            }
        } catch (error: any) {
            console.error('Error fetching WhatsApp numbers:', error);
            setErrorMessage(error.message || 'Failed to fetch WhatsApp phone numbers');
        } finally {
            setLoadingType(null);
        }
    };
    
    // Load user assignments for WhatsApp numbers
    const loadUserAssignments = async (accountId: string, wabaAccountId: string, numbers: WhatsAppNumber[]) => {
        if (!userData?.companyId) return;
        
        try {
            // SIMPLIFIED APPROACH:
            // 1. Create a new object to hold assignments
            // 2. Directly set it as state without complex transformations
            const directAssignments: {[key: string]: string[]} = {};
            
            // Iterate through each number document and check for assigned users
            for (const number of numbers) {
                const numberRef = doc(
                    db, 
                    'whatsappBusinessCloudAccounts', 
                    accountId, 
                    'businesses', 
                    selectedBusinessId || 'unknown', 
                    'wabaAccounts', 
                    wabaAccountId, 
                    'phoneNumbers', 
                    number.id
                );
                
                const numberDoc = await getDoc(numberRef);
                
                if (numberDoc.exists()) {
                    const data = numberDoc.data();
                    if (data.assignedUsers && Array.isArray(data.assignedUsers) && data.assignedUsers.length > 0) {
                        // Store directly with no transformation
                        directAssignments[number.id] = [...data.assignedUsers];
                    }
                }
            }
            // Directly set the state and force a re-render
            setNumberAssignedUsers(directAssignments);
            setLastUpdateTime(new Date().getTime());
        } catch (error) {
            console.error('Error loading user assignments:', error);
        }
    };
    
    // Function to select or deselect all users
    const handleSelectAllUsers = (select: boolean) => {
        const newSelectedUsers: {[key: string]: boolean} = {};
        
        // If we're filtering users, only select/deselect the filtered ones
        const usersToUpdate = userSearch.trim() ? 
            companyUsers.filter(user => 
                (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
            ) : 
            companyUsers;
        
        // Copy existing selections
        Object.keys(selectedUsers).forEach(id => {
            newSelectedUsers[id] = selectedUsers[id];
        });
        
        // Update with new selections
        usersToUpdate.forEach(user => {
            newSelectedUsers[user.id] = select;
        });
        
        setSelectedUsers(newSelectedUsers);
    };
    
    // Handle user assignment to a WhatsApp number
    const handleAssignUsers = (numberId: string) => {
        // First fetch company users if not already loaded
        if (companyUsers.length === 0) {
            fetchCompanyUsers();
        }
        
        setCurrentNumberBeingAssigned(numberId);
        
        // Pre-select users who are already assigned to this number
        const preSelectedUsers: {[key: string]: boolean} = {};
        const assignedUsers = numberAssignedUsers[numberId] || [];
        
        companyUsers.forEach(user => {
            preSelectedUsers[user.id] = assignedUsers.includes(user.id);
        });
        
        setSelectedUsers(preSelectedUsers);
        setShowUserAssignmentModal(true);
    };
    
    // Handle confirming user assignment
    const handleConfirmUserAssignment = async () => {
        if (!accessToken || !currentNumberBeingAssigned || !userData?.companyId) return;
        
        setIsLoading(true);
        try {
            // Get the Facebook account ID
            const userResponse = await fetch(
                `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
            );
            
            if (!userResponse.ok) {
                throw new Error('Failed to get account ID');
            }
            
            const fbUserData = await userResponse.json();
            const accountId = fbUserData.id;
            
            // Get selected user IDs
            const selectedUserIds = Object.keys(selectedUsers).filter(id => selectedUsers[id]);
            
            // Only proceed if users are actually assigned
            if (selectedUserIds.length === 0) {
                throw new Error('No users selected for assignment');
            }
            
            // Find the number details from state
            const numberDetails = whatsappNumbers.find(number => number.id === currentNumberBeingAssigned);
            
            // Get the real WABA account ID from selected accounts, don't extract from phone number ID
            const account = whatsappAccounts.find(a => a.id === selectedAccountId);
            if (!account) {
                throw new Error('WhatsApp Business Account not found');
            }
            const wabaId = account.id;
            
            if (!numberDetails) {
                throw new Error('Phone number details not found');
            }
            
            const batch = writeBatch(db);
            
            // Get the actual expiration date from Facebook
            const expirationDate = await getTokenExpiration(accessToken);
            
            // Save account info with the actual expiration date
            const accountRef = doc(db, 'whatsappBusinessCloudAccounts', accountId);
            batch.set(accountRef, {
                accountId: accountId,
                accountName: fbUserData.name || 'WhatsApp Business Account',
                accountAccessToken: accessToken,
                accountExpiration: expirationDate,
                companyId: userData.companyId,
                updatedAt: serverTimestamp()
            }, { merge: true });
            
            // Save business info
            const business = businesses.find(b => b.id === selectedBusinessId);
            if (business) {
                const businessRef = doc(db, 'whatsappBusinessCloudAccounts', accountId, 'businesses', business.id);
                batch.set(businessRef, {
                    businessId: business.id,
                    name: business.name,
                    companyId: userData.companyId,
                    updatedAt: serverTimestamp()
                }, { merge: true });
            }
            
            // Save WhatsApp account info
            if (account) {
                const wabaRef = doc(db, 'whatsappBusinessCloudAccounts', accountId, 'businesses', selectedBusinessId || 'unknown', 'wabaAccounts', wabaId);
                batch.set(wabaRef, {
                    wabaId: wabaId,
                    name: account.name,
                    companyId: userData.companyId,
                    updatedAt: serverTimestamp()
                }, { merge: true });
            }
            
            // Save the phone number document with assigned users
            const numberRef = doc(db, 'whatsappBusinessCloudAccounts', accountId, 'businesses', selectedBusinessId || 'unknown', 'wabaAccounts', wabaId, 'phoneNumbers', currentNumberBeingAssigned);
            batch.set(numberRef, {
                numberId: numberDetails.id,
                displayPhoneNumber: numberDetails.display_phone_number,
                verifiedName: numberDetails.verified_name,
                qualityRating: numberDetails.quality_rating || 'UNKNOWN',
                status: numberDetails.status || 'CONNECTED',
                assignedUsers: selectedUserIds,
                companyId: userData.companyId,
                updatedAt: serverTimestamp()
            }, { merge: true });
            
            // Commit all changes at once
            await batch.commit();
            
            // Add selected users to RTDB for WhatsApp access
            try {
                await addUsersToRTDB(selectedUserIds, userData.companyId);
                console.log('Users added to RTDB successfully');
            } catch (rtdbError) {
                console.error('Error adding users to RTDB:', rtdbError);
                // We'll continue even if RTDB update fails, but log the error
                // This is to ensure the Firestore updates aren't lost
            }
            
            // Update local state with deep copy to ensure React detects the state change
            const updatedAssignments = {...numberAssignedUsers};
            updatedAssignments[currentNumberBeingAssigned] = [...selectedUserIds];
            
            // Force state update
            setNumberAssignedUsers(updatedAssignments);
            
            // Show success message
            setToastMessage('Users assigned successfully');
            setIsErrorToast(false);
            setShowToast(true);
            
            // Close modal
            setShowUserAssignmentModal(false);
            setCurrentNumberBeingAssigned(null);
            setSelectedUsers({});
        } catch (error) {
            console.error('Error assigning users:', error);
            
            // Show error message
            setToastMessage('Failed to assign users: ' + (error instanceof Error ? error.message : 'Unknown error'));
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Fetch company users for assignment
    const fetchCompanyUsers = async () => {
        if (!userData?.companyId) return;
        
        try {
            setLoadingType('users');
            
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', userData.companyId));
            const querySnapshot = await getDocs(q);
            
            const users: User[] = [];
            querySnapshot.forEach((doc) => {
                const userData = doc.data() as User;
                users.push({
                    ...userData,
                    id: doc.id
                });
            });
            
            setCompanyUsers(users);
        } catch (error) {
            console.error('Error fetching company users:', error);
        } finally {
            setLoadingType(null);
        }
    };
    
    // Function to add users to RTDB for WhatsApp access
    const addUsersToRTDB = async (userIds: string[], companyId: string) => {
        try {
            const functions = getFunctions();
            const batchAddUsersToRTDBFunction = httpsCallable(functions, 'batchAddUsersToRTDB');
            
            const result = await batchAddUsersToRTDBFunction({ 
                userIds, 
                companyId 
            });
            
            console.log('RTDB User assignment result:', result.data);
            return result.data;
        } catch (error) {
            console.error('Error adding users to RTDB:', error);
            throw error; // Re-throw to handle in the calling function
        }
    };

    // Load token from localStorage or extract from URL on component mount
    useEffect(() => {
        // Check for token in URL hash
        const getTokenFromUrl = () => {
            const hash = window.location.hash;
            if (hash) {
                const tokenMatch = /access_token=([^&]+)/.exec(hash);
                if (tokenMatch) {
                    const token = tokenMatch[1];
                    // Clear the URL hash
                    window.history.replaceState({}, document.title, window.location.pathname);
                    return token;
                }
            }
            return null;
        };
        
        const tokenFromUrl = getTokenFromUrl();
        
        if (tokenFromUrl) {
            (async () => {
                // Verify the Facebook account before saving the token
                const isAccountVerified = await verifyFacebookAccount(tokenFromUrl);
                if (isAccountVerified) {
                    saveTokenToStorage(tokenFromUrl);
                    setAccessToken(tokenFromUrl);
                }
            })();
        } else {
            // Check token in localStorage
            const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
            const expiry = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
            
            if (token && expiry) {
                const expiryTime = parseInt(expiry, 10);
                const now = new Date().getTime();
                
                if (now < expiryTime) {
                    (async () => {
                        // Verify the Facebook account before using the stored token
                        const isAccountVerified = await verifyFacebookAccount(token);
                        if (isAccountVerified) {
                            setAccessToken(token);
                        }
                    })();
                } else {
                    setTokenExpired(true);
                    clearTokenFromStorage();
                }
            }
        }
    }, []);
    
    // Fetch businesses when token is available
    useEffect(() => {
        if (accessToken) {
            fetchBusinesses();
        }
    }, [accessToken]);
    
    // Fetch WhatsApp Business accounts when a business is selected
    useEffect(() => {
        if (accessToken && selectedBusinessId) {
            fetchWhatsAppAccounts(selectedBusinessId);
        }
    }, [accessToken, selectedBusinessId]);
    
    // Fetch phone numbers when a WhatsApp Business account is selected
    useEffect(() => {
        if (accessToken && selectedAccountId) {
            fetchWhatsAppNumbers(selectedAccountId);
        }
    }, [accessToken, selectedAccountId]);
    
    // Load company users whenever we load the component
    useEffect(() => {
        const loadCompanyUsers = async () => {
            if (!userData?.companyId) return;
            
            try {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where('companyId', '==', userData.companyId));
                const querySnapshot = await getDocs(q);
                const users = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as User[];
                setCompanyUsers(users);
            } catch (error) {
                console.error('Error loading company users:', error);
            }
        };
        
        loadCompanyUsers();
    }, [userData]);
    
    // Additional effect to ensure user assignments are loaded properly
    // This runs after company users are loaded
    useEffect(() => {
        // If we have phone numbers but no assignments, try to reload assignments
        if (whatsappNumbers.length > 0 && Object.keys(numberAssignedUsers).length === 0 && selectedAccountId && accessToken) {
            // Find the account to get the ID
            const account = whatsappAccounts.find(a => a.id === selectedAccountId);
            if (account) {
                const loadAssignments = async () => {
                    try {
                        // Get the Facebook user ID
                        const userResponse = await fetch(
                            `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
                        );
                        
                        if (userResponse.ok) {
                            const fbUserData = await userResponse.json();
                            const fbAccountId = fbUserData.id;
                            
                            await loadUserAssignments(fbAccountId, account.id, whatsappNumbers);
                        }
                    } catch (error) {
                        console.error('Error in assignment reload effect:', error);
                    }
                };
                
                loadAssignments();
            }
        }
    }, [whatsappNumbers, numberAssignedUsers, selectedAccountId, accessToken, companyUsers, lastUpdateTime]);
    
    // Render component
    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-8 rounded-3xl shadow-md">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <button 
                                onClick={handleGoBack}
                                className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back to Integrations
                            </button>
                            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Business Cloud API</h1>
                            <p className="text-gray-600 mt-2">Connect your WhatsApp Business account to communicate with your customers</p>
                        </div>
                        <div className="flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-green-500">
                            <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.188 8.188 0 0 1-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24M8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.82-.78.99-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01" />
                        </svg>
                        </div>
                    </div>
                        {/* Connect Button Section */}
                    <div className="mb-8 text-center">
                        <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Connect Your WhatsApp Business Account</h2>
                            
                            {accessToken ? (
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                            <span className="h-2 w-2 mr-2 bg-green-500 rounded-full"></span>
                                            Connected Successfully
                                        </span>
                                        <button
                                            onClick={handleDisconnectFacebook}
                                            className="text-sm text-red-600 hover:text-red-800 px-3 py-1 hover:bg-red-50 rounded-md transition-colors">
                                            Disconnect
                                        </button>
                                    </div>
                                    
                                    {tokenExpired && (
                                        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-lg mb-4">
                                            <div className="flex items-start">
                                                <svg className="h-5 w-5 text-amber-400 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <div>
                                                    <h3 className="text-sm font-medium">Session Expired</h3>
                                                    <p className="text-xs mt-1">Your Meta session has expired. Please reconnect to continue accessing your WhatsApp business account.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {errorMessage && (
                                        <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">
                                            {errorMessage}
                                        </div>
                                    )}
                                    
                                    {/* Step-by-step selection process */}
                                    <div className="mt-8">
                                        {/* Step 1: Select a Business */}
                                        <div className="mb-6">
                                            <h3 className="text-lg font-medium text-gray-800 mb-3">Step 1: Select a Business</h3>
                                            {loadingType === 'businesses' ? (
                                                <div className="p-4 text-center">
                                                    <svg className="animate-spin h-6 w-6 mx-auto text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <p className="mt-2 text-sm text-gray-600">Loading businesses...</p>
                                                </div>
                                            ) : businesses.length > 0 ? (
                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                                    {businesses.map(business => (
                                                        <div 
                                                            key={business.id}
                                                            onClick={() => {
                                                                // Clear previous WhatsApp account data when changing businesses
                                                                setSelectedBusinessId(business.id);
                                                                setWhatsappAccounts([]);
                                                                setSelectedAccountId(null);
                                                                setWhatsappNumbers([]);
                                                                setNumberAssignedUsers({});
                                                            }}
                                                            className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedBusinessId === business.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                                                        >
                                                            <h4 className="font-medium text-left text-gray-900">{business.name}</h4>
                                                            <div className="mt-2 flex justify-between items-center">
                                                                <span className="text-xs text-gray-500">ID: {business.id}</span>
                                                                {selectedBusinessId === business.id && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                        Selected
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 p-4 rounded-lg">
                                                    <p>No businesses found. Please make sure you have access to a Meta Business account.</p>
                                                    <button 
                                                        onClick={fetchBusinesses}
                                                        className="mt-2 px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-sm rounded transition-colors"
                                                    >
                                                        Refresh
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Step 2: Select a WhatsApp Business Account (only show if business is selected) */}
                                        {selectedBusinessId && (
                                            <div className="mb-6">
                                                <h3 className="text-lg font-medium text-gray-800 mb-3">Step 2: Select a WhatsApp Business Account</h3>
                                                {loadingType === 'accounts' ? (
                                                    <div className="p-4 text-center">
                                                        <svg className="animate-spin h-6 w-6 mx-auto text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <p className="mt-2 text-sm text-gray-600">Loading WhatsApp accounts...</p>
                                                    </div>
                                                ) : whatsappAccounts.length > 0 ? (
                                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                                        {whatsappAccounts.map(account => (
                                                            <div
                                                                key={account.id}
                                                                onClick={() => {
                                                                    // Clear previous phone number data when changing WhatsApp accounts
                                                                    setSelectedAccountId(account.id);
                                                                    setWhatsappNumbers([]);
                                                                    setNumberAssignedUsers({});
                                                                    setSelectedNumberId(null);
                                                                }}
                                                                className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedAccountId === account.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
                                                            >
                                                                <h4 className="font-medium text-left text-gray-900">{account.name}</h4>
                                                                {account.business_name && (
                                                                    <p className="text-sm text-left text-gray-600 mt-1">{account.business_name}</p>
                                                                )}
                                                                <div className="mt-2 flex justify-between items-center">
                                                                    <span className="text-xs text-gray-500">ID: {account.id}</span>
                                                                    {selectedAccountId === account.id && (
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                            Selected
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Templates Button */}
                                                                <div className="mt-3 text-right">
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation(); // Prevent selecting the account when clicking the button
                                                                            setCurrentTemplateAccount({id: account.id, name: account.name});
                                                                            setShowTemplatesModal(true);
                                                                        }}
                                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                                                        </svg>
                                                                        Templates
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 p-4 rounded-lg">
                                                        <p>No WhatsApp Business accounts found. Please make sure your business has a WhatsApp Business account.</p>
                                                        <button
                                                            onClick={() => fetchWhatsAppAccounts(selectedBusinessId)}
                                                            className="mt-2 px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-sm rounded transition-colors"
                                                        >
                                                            Refresh
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Step 3: Manage WhatsApp Phone Numbers (only show if WhatsApp account is selected) */}
                                        {selectedAccountId && (
                                            <div className="mb-6">
                                                <h3 className="text-lg font-medium text-gray-800 mb-3">Step 3: Manage Phone Numbers & Assign Users</h3>
                                                {loadingType === 'numbers' ? (
                                                    <div className="p-4 text-center">
                                                        <svg className="animate-spin h-6 w-6 mx-auto text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <p className="mt-2 text-sm text-gray-600">Loading phone numbers...</p>
                                                    </div>
                                                ) : whatsappNumbers.length > 0 ? (
                                                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                                                        <table className="min-w-full">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                                                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified Name</th>
                                                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality Rating</th>
                                                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Users</th>
                                                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200">
                                                                {whatsappNumbers.map((number) => (
                                                                    <tr key={number.id} className={`hover:bg-gray-50 ${selectedNumberId === number.id ? 'bg-green-50' : ''}`}>
                                                                        <td className="py-3 px-4 text-sm text-left text-gray-900">{number.display_phone_number}</td>
                                                                        <td className="py-3 px-4 text-sm text-left text-gray-900">{number.verified_name}</td>
                                                                        <td className="py-3 px-4 text-sm text-left text-gray-900">
                                                                            {number.quality_rating ? (
                                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${number.quality_rating === 'GREEN' ? 'bg-green-100 text-green-800' : number.quality_rating === 'YELLOW' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                                    {number.quality_rating}
                                                                                </span>
                                                                            ) : 'N/A'}
                                                                        </td>
                                                                        <td className="py-3 px-4 text-sm text-left text-gray-900">
                                                                            {number.status ? (
                                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                    {number.status}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                    Connected
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td className="py-3 px-4 text-sm text-left text-gray-900 relative group">
                                                                            {/* Simple direct check for assigned users */}
                                                                            {numberAssignedUsers && numberAssignedUsers[number.id] && numberAssignedUsers[number.id].length > 0 ? (
                                                                                <div key={`assigned-users-${number.id}-${numberAssignedUsers[number.id].length}`}>
                                                                                    <div className="flex items-center -space-x-2">
                                                                                        {numberAssignedUsers[number.id].slice(0, 3).map((userId) => {
                                                                                            // Provide fallback when users aren't loaded yet
                                                                                            const foundUser = companyUsers.find(u => u.id === userId);
                                                                                            const user = foundUser || { id: userId, name: userId.substring(0, 2).toUpperCase(), email: '', photoURL: null };
                                                                                            return user ? (
                                                                                                <div key={userId} className="h-8 w-8 rounded-full bg-white ring-2 ring-white overflow-hidden">
                                                                                                    {user.photoURL ? (
                                                                                                        <img src={user.photoURL} alt={user.name || 'User'} className="h-full w-full object-cover" />
                                                                                                    ) : (
                                                                                                        <div className="h-full w-full bg-green-100 flex items-center justify-center">
                                                                                                            <span className="text-green-800 font-medium text-xs">
                                                                                                                {user.name ? user.name.split(' ').map(n => n[0]).join('') : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                                                            </span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            ) : null;
                                                                                        })}
                                                                                        {numberAssignedUsers[number.id].length > 3 && (
                                                                                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-white">
                                                                                                <span className="text-gray-700 text-xs font-medium">+{numberAssignedUsers[number.id].length - 3}</span>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    
                                                                                    {/* Tooltip that appears on hover */}
                                                                                    <div className="hidden group-hover:block absolute z-10 mt-1 w-48 bg-white shadow-lg rounded-md p-2 text-xs border border-gray-200 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                                                        <div className="py-1 font-medium text-gray-600 border-b border-gray-100 mb-1">Assigned users:</div>
                                                                                        <div className="max-h-40 overflow-y-auto">
                                                                                            {numberAssignedUsers[number.id].map(userId => {
                                                                                                const user = companyUsers.find(u => u.id === userId);
                                                                                                return user ? (
                                                                                                    <div key={userId} className="flex items-center py-1">
                                                                                                        <div className="mr-2 h-5 w-5 rounded-full bg-green-100 flex-shrink-0 overflow-hidden">
                                                                                                            {user.photoURL ? (
                                                                                                                <img src={user.photoURL} alt={user.name || 'User'} className="h-full w-full object-cover" />
                                                                                                            ) : (
                                                                                                                <div className="h-full w-full flex items-center justify-center">
                                                                                                                    <span className="text-green-800 text-xs">
                                                                                                                        {user.name ? user.name.charAt(0).toUpperCase() : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                                                                    </span>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                        <div className="truncate">
                                                                                                            <div className="font-medium">{user.name || 'Unnamed'}</div>
                                                                                                            <div className="text-gray-500 text-xs">{user.email}</div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                ) : null;
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-xs text-left text-gray-500 flex items-center">
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                                    </svg>
                                                                                    No users assigned
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td className="py-3 px-4 text-sm text-left space-x-2">
                                                                            <button
                                                                                onClick={() => handleAssignUsers(number.id)}
                                                                                className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm rounded-md transition-colors"
                                                                            >
                                                                                Assign Users
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 p-4 rounded-lg">
                                                        <p>No phone numbers found for this WhatsApp Business account.</p>
                                                        <button
                                                            onClick={() => fetchWhatsAppNumbers(selectedAccountId)}
                                                            className="mt-2 px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-sm rounded transition-colors"
                                                        >
                                                            Refresh
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center mb-8">
                                    <div className="bg-green-50 p-4 rounded-lg mb-6">
                                        <p className="text-gray-800 mb-3">Connect ScholarCRM with your WhatsApp Business account to:</p>
                                        <ul className="text-gray-700 text-left list-disc pl-5 space-y-2">
                                            <li>Communicate with customers through WhatsApp</li>
                                            <li>Send and receive messages via a business number</li>
                                            <li>Manage multiple WhatsApp phone numbers in one place</li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                            
                            <button
                                onClick={handleConnectToFacebook}
                                className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all flex items-center gap-2 mx-auto"
                                aria-label="Connect to WhatsApp"
                                disabled={isLoading}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                    <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91c0 1.75.46 3.38 1.25 4.8l-1.3 4.75c-.08.29.02.6.23.82c.19.2.47.28.74.22l4.85-1.25c1.4.75 2.97 1.17 4.64 1.17c5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2z" />
                                </svg>
                                {accessToken ? 'Reconnect to WhatsApp Business Account' : 'Connect to WhatsApp Business Account'}
                            </button>
                        </div>
                    </div>
                    
                    {/* WhatsApp numbers section has been merged into Step 3 */}
                    
                    {/* User Assignment Modal */}
                    {showUserAssignmentModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
                                <div className="p-6 border-b border-gray-200">
                                    <h3 className="text-xl font-semibold text-gray-900">Assign Users to WhatsApp Number</h3>
                                    <p className="mt-2 text-sm text-gray-600">
                                        {whatsappNumbers.find(n => n.id === currentNumberBeingAssigned)?.display_phone_number}
                                    </p>
                                </div>
                                
                                <div className="p-6 flex-1 overflow-y-auto">
                                    {/* Search and selection controls */}
                                    <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="relative md:w-1/2">
                                            <input
                                                type="text"
                                                placeholder="Search users..."
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                            />
                                            <div className="absolute right-3 top-2.5 text-gray-400">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleSelectAllUsers(true)}
                                                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                                            >
                                                Select All
                                            </button>
                                            <button
                                                onClick={() => handleSelectAllUsers(false)}
                                                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* User list */}
                                    <div className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-96 overflow-y-auto">
                                        {companyUsers.length > 0 ? (
                                            companyUsers
                                                .filter(user => 
                                                    userSearch.trim() === '' || 
                                                    (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                    (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                    (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                                )
                                                .map(user => (
                                                    <div key={user.id} className="flex items-center p-3 hover:bg-gray-50">
                                                        <input
                                                            type="checkbox"
                                                            id={`user-${user.id}`}
                                                            checked={selectedUsers[user.id] || false}
                                                            onChange={(e) => setSelectedUsers({...selectedUsers, [user.id]: e.target.checked})}
                                                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                                        />
                                                        <label htmlFor={`user-${user.id}`} className="ml-3 flex-1 cursor-pointer">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                                                    {user.photoURL ? (
                                                                        <img src={user.photoURL} alt="" className="h-8 w-8 rounded-full" />
                                                                    ) : (
                                                                        <span className="text-sm font-medium text-gray-600">
                                                                            {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="ml-3 truncate">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">{user.name || 'Unnamed User'}</p>
                                                                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                                                </div>
                                                                {user.role && (
                                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                                        {user.role}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </label>
                                                    </div>
                                                )
                                            )
                                        ) : loadingType === 'users' ? (
                                            <div className="flex items-center justify-center p-8">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500 mr-3"></div>
                                                <span>Loading users...</span>
                                            </div>
                                        ) : (
                                            <div className="p-4 text-center text-gray-500">
                                                No users found
                                            </div>
                                        )}
                                        
                                        {companyUsers.length > 0 && userSearch.trim() !== '' && 
                                            companyUsers.filter(user => 
                                                (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                            ).length === 0 && (
                                                <div className="p-4 text-center text-gray-500">
                                                    No users match your search
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                                
                                <div className="p-6 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
                                    <div className="mb-4 md:mb-0 text-sm text-gray-500">
                                        {Object.values(selectedUsers).filter(Boolean).length} users selected
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => {
                                                setShowUserAssignmentModal(false);
                                                setCurrentNumberBeingAssigned(null);
                                                setSelectedUsers({});
                                            }}
                                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleConfirmUserAssignment}
                                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                                            disabled={isLoading}
                                        >
                                            {isLoading && (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            )}
                                            Confirm
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Help & Support Section */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl shadow-sm mt-8">
                        <div className="flex items-start">
                            <div className="bg-white p-2 rounded-lg shadow-sm mr-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Need Help?</h3>
                                <p className="text-gray-700 text-sm mb-4">For assistance with setting up your WhatsApp Business integration or connecting phone numbers, please contact our support team.</p>
                                <a 
                                    href="mailto:support@scholarcred.com" 
                                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    {/* Toast notification */}
                    {showToast && (
                        <Toast 
                            message={toastMessage}
                            isError={isErrorToast}
                            onClose={() => setShowToast(false)}
                            show={showToast}
                        />
                    )}
                    
                    {/* Templates Modal */}
                    {showTemplatesModal && currentTemplateAccount && (
                        <WhatsappBusinessCloudTemplates
                            accountId={currentTemplateAccount.id}
                            accountName={currentTemplateAccount.name}
                            accessToken={accessToken || ''}
                            onClose={() => {
                                setShowTemplatesModal(false);
                                setCurrentTemplateAccount(null);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default WhatsappBusinessCloud;
